import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useRememberPath } from "@/hooks/use-remember-path";
import { useServerFn } from "@tanstack/react-start";
import { GATES, getGate, nextGate } from "@/lib/gates";
import { itemsForGate } from "@/lib/knowledge";
import { KIND_LABEL, KIND_NOUN, type EcosystemItem, type ItemKind } from "@/lib/ecosystem";
import { getGatePractice, type GateAction } from "@/lib/gate-practice";
import {
  getGateProgress,
  recordGateAction,
  type GateProgressRow,
} from "@/lib/gates.functions";
import { track } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { CodexRail } from "@/components/site/CodexRail";
import { Check } from "lucide-react";
import { absoluteUrl } from "@/lib/site";
import { breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";


export const Route = createFileRoute("/gates/$n")({
  loader: ({ params }) => {
    const n = Number(params.n);
    if (!Number.isInteger(n) || !getGate(n)) throw notFound();
    return { n };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Gate not found — KAfrequency" }, { name: "robots", content: "noindex" }] };
    }
    const g = getGate(loaderData.n)!;
    const path = `/gates/${g.n}`;
    const url = absoluteUrl(path);
    const nextG = nextGate(g.n);

    const term = {
      "@type": "DefinedTerm",
      "@id": `${url}#gate`,
      name: `Gate ${g.n}: ${g.title}`,
      description: g.reflection,
      url,
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        "@id": `${absoluteUrl("/gates")}#gates`,
        name: "The 47 Gates of Consciousness",
        url: absoluteUrl("/gates"),
      },
      ...(nextG
        ? {
            isRelatedTo: {
              "@type": "DefinedTerm",
              "@id": `${absoluteUrl(`/gates/${nextG.n}`)}#gate`,
              name: `Gate ${nextG.n}: ${nextG.title}`,
              url: absoluteUrl(`/gates/${nextG.n}`),
            },
          }
        : {}),
    };

    return {
      ...seoMeta({
        title: `Gate ${g.n}: ${g.title} · The 47 Gates`,
        description: g.reflection,
        path,
        ogType: "article",
      }),
      scripts: [
        ldScript(
          pageNode({
            type: "WebPage",
            id: `${url}#page`,
            url,
            name: `Gate ${g.n}: ${g.title}`,
            description: g.reflection,
            mainEntity: term,
            about: [personRef(), orgRef()],
            extra: { author: personRef(), publisher: orgRef() },
          }),
        ),
        ldScript(breadcrumbs([["The 47 Gates", "/gates"], [`Gate ${g.n}: ${g.title}`, path]])),
      ],
    };
  },


  component: GateDetail,
  notFoundComponent: GateNotFound,
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <p className="text-sm text-muted-foreground">{error.message}</p>
      <Link to="/gates" className="mt-6 inline-block text-gold hover:text-gold-luminous">← Return to the Gates</Link>
    </div>
  ),
});

function GateDetail() {
  const { n } = Route.useLoaderData();
  const gate = getGate(n)!;
  const next = nextGate(n);
  const practice = getGatePractice(n);
  const sibling = getGate(practice.siblingGate);
  const related = itemsForGate(n, 12);

  const loadProgress = useServerFn(getGateProgress);
  const record = useServerFn(recordGateAction);
  const [signedIn, setSignedIn] = useState(false);
  const [progress, setProgress] = useState<GateProgressRow | null>(null);
  const [busy, setBusy] = useState<GateAction | null>(null);

  // Phase 3B — remember the gate so the visitor can return to it.
  useRememberPath({
    kind: "gate",
    ref: String(n),
    title: `Gate ${n} · ${gate.title}`,
    href: `/gates/${n}`,
    countsAsStart: true,
    completed: Boolean(progress?.completed_at),
  });

  useEffect(() => {
    track("gate_view", { n });
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      const has = !!data.user;
      setSignedIn(has);
      if (has) {
        loadProgress({ data: { gate_n: n } })
          .then((row) => {
            if (!cancelled) setProgress(row);
            // Auto-record "read_reflection" on view for signed-in members.
            if (!cancelled && (!row || !row.actions.includes("read_reflection"))) {
              record({ data: { gate_n: n, action: "read_reflection" } })
                .then((r) => !cancelled && setProgress(r))
                .catch(() => void 0);
            }
          })
          .catch(() => void 0);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [n, loadProgress, record]);

  const has = (a: GateAction) => !!progress?.actions.includes(a);
  const doAction = async (a: GateAction) => {
    if (!signedIn || busy) return;
    setBusy(a);
    try {
      const row = await record({ data: { gate_n: n, action: a } });
      setProgress(row);
    } finally {
      setBusy(null);
    }
  };

  const grouped: Record<ItemKind, EcosystemItem[]> = { music: [], wisdom: [], activation: [], book: [] };
  for (const item of related) grouped[item.kind].push(item);

  const sections: Array<{ kind: ItemKind; heading: string }> = [
    { kind: "activation", heading: "Practice" },
    { kind: "music", heading: "Listen" },
    { kind: "wisdom", heading: "Read" },
    { kind: "book", heading: "Study" },
  ];

  const walked = !!progress?.completed_at;

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="mb-8 text-xs uppercase tracking-[0.3em] text-muted-foreground">
        <Link to="/gates" className="hover:text-gold">The 47 Gates</Link>
        <span className="mx-2 text-gold/50">·</span>
        <span className="text-gold">Gate {gate.n}</span>
      </nav>

      <header className="relative overflow-hidden rounded-3xl border hairline bg-obsidian/60 p-8 backdrop-blur sm:p-12">
        <div aria-hidden className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-gold/10 blur-3xl" />
        <div className="flex items-start justify-between gap-4">
          <p className="text-[10px] uppercase tracking-[0.4em] text-gold">
            Gate {String(gate.n).padStart(2, "0")} of {GATES.length}
          </p>
          {walked && (
            <span className="inline-flex items-center gap-1 rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-gold">
              <Check size={12} /> Walked
            </span>
          )}
        </div>
        <h1 className="mt-4 font-serif text-4xl leading-tight text-gold-luminous sm:text-6xl">{gate.title}</h1>
        <p className="mt-6 font-serif text-lg italic leading-relaxed text-ivory/90 sm:text-xl">
          &ldquo;{gate.reflection}&rdquo;
        </p>
        {gate.expanded && <p className="mt-6 text-base text-muted-foreground sm:text-lg">{gate.expanded}</p>}
      </header>

      {/* Practice card */}
      <section aria-labelledby="gate-practice" className="mt-10 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border hairline bg-obsidian/40 p-6">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Breath</p>
          <h2 id="gate-practice" className="mt-2 font-serif text-xl text-ivory">{practice.breath.label}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Inhale {practice.breath.inhale}
            {practice.breath.holdIn ? ` · hold ${practice.breath.holdIn}` : ""} · exhale {practice.breath.exhale}
            {practice.breath.holdOut ? ` · hold ${practice.breath.holdOut}` : ""} · {practice.breath.cycles} cycles
          </p>
          <ActionButton
            done={has("did_breath")}
            busy={busy === "did_breath"}
            disabled={!signedIn}
            onClick={() => doAction("did_breath")}
            label="Mark breath complete"
            doneLabel="Breath practiced"
          />
        </div>

        <div className="rounded-2xl border hairline bg-obsidian/40 p-6">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Creative</p>
          <p className="mt-3 font-serif text-lg text-ivory">{practice.creative}</p>
          <ActionButton
            done={has("did_creative")}
            busy={busy === "did_creative"}
            disabled={!signedIn}
            onClick={() => doAction("did_creative")}
            label="Mark creative complete"
            doneLabel="Creative practiced"
          />
        </div>
      </section>

      {/* Journal */}
      <section aria-labelledby="journal-prompt" className="mt-10 rounded-2xl border hairline bg-obsidian/40 p-8 sm:p-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Sit with</p>
        <h2 id="journal-prompt" className="mt-3 font-serif text-2xl text-ivory sm:text-3xl">
          {gate.journalPrompt}
        </h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to="/journey/journal"
            search={{ gate: String(n) } as never}
            onClick={() => signedIn && doAction("wrote_journal")}
            className="inline-flex items-center rounded-full border border-gold/60 px-5 py-2 text-sm text-gold hover:bg-gold hover:text-obsidian"
          >
            Write in your journal
          </Link>
          {has("wrote_journal") && (
            <span className="inline-flex items-center gap-1 text-xs text-gold">
              <Check size={12} /> Journaled
            </span>
          )}
        </div>
      </section>

      <CodexRail entityKind="gate" entityRef={String(n)} />

      {/* Resonant transmissions */}
      {related.length > 0 && (
        <section aria-labelledby="gate-resonance" className="mt-16">
          <div className="flex items-baseline justify-between border-b hairline pb-3">
            <h2 id="gate-resonance" className="font-serif text-2xl">Resonant transmissions</h2>
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
              {related.length} item{related.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="mt-6 space-y-10">
            {sections.map(({ kind, heading }) => {
              const list = grouped[kind];
              if (!list.length) return null;
              return (
                <div key={kind}>
                  <h3 className="font-serif text-lg text-gold">
                    {heading} <span className="ml-1 text-xs text-muted-foreground">· {KIND_LABEL[kind]}</span>
                  </h3>
                  <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                    {list.slice(0, 6).map((item) => (
                      <li key={`${item.kind}:${item.slug}`}>
                        <ResonantLink
                          item={item}
                          onPress={() => {
                            track("gate_engage", { n, kind: item.kind, slug: item.slug });
                            if (signedIn) {
                              if (item.kind === "music") doAction("played_music");
                              else if (item.kind === "activation") doAction("finished_meditation");
                            }
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Next & sibling */}
      <nav aria-label="Suggested next gates" className="mt-20 grid gap-4 sm:grid-cols-2">
        <Link
          to="/gates/$n"
          params={{ n: String(next.n) }}
          className="group rounded-2xl border hairline p-6 hover:border-gold"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Suggested next gate →</p>
          <p className="mt-2 font-serif text-xl text-ivory group-hover:text-gold-luminous">
            Gate {next.n} · {next.title.replace(/^The Gate of /, "")}
          </p>
        </Link>
        {sibling && sibling.n !== next.n && (
          <Link
            to="/gates/$n"
            params={{ n: String(sibling.n) }}
            className="group rounded-2xl border hairline p-6 text-right hover:border-gold"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Thematic sibling</p>
            <p className="mt-2 font-serif text-xl text-ivory group-hover:text-gold-luminous">
              Gate {sibling.n} · {sibling.title.replace(/^The Gate of /, "")}
            </p>
          </Link>
        )}
      </nav>

      {!signedIn && (
        <p className="mt-10 text-center text-xs text-muted-foreground">
          <Link to="/auth" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
            Sign in
          </Link>{" "}
          to record your journey through the Gates.
        </p>
      )}
    </div>
  );
}

function ActionButton({
  done,
  busy,
  disabled,
  onClick,
  label,
  doneLabel,
}: {
  done: boolean;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
  label: string;
  doneLabel: string;
}) {
  if (done) {
    return (
      <p className="mt-4 inline-flex items-center gap-1 text-xs text-gold">
        <Check size={12} /> {doneLabel}
      </p>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className="mt-4 inline-flex items-center rounded-full border border-gold/40 px-4 py-1.5 text-xs text-gold hover:border-gold hover:bg-gold/10 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy ? "…" : label}
    </button>
  );
}

function ResonantLink({ item, onPress }: { item: EcosystemItem; onPress: () => void }) {
  const label = KIND_NOUN[item.kind];
  const common =
    "group flex h-full flex-col gap-1 rounded-xl border hairline bg-obsidian/60 p-4 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80";
  const inner = (
    <>
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</p>
      <p className="font-serif text-base text-ivory group-hover:text-gold-luminous">{item.title}</p>
      <p className="text-xs text-muted-foreground">{item.subtitle}</p>
    </>
  );
  if (item.kind === "music") return <Link to="/music/$slug" params={{ slug: item.slug }} onClick={onPress} className={common}>{inner}</Link>;
  if (item.kind === "wisdom") return <Link to="/wisdom/$slug" params={{ slug: item.slug }} onClick={onPress} className={common}>{inner}</Link>;
  if (item.kind === "activation") return <Link to="/activations/$slug" params={{ slug: item.slug }} onClick={onPress} className={common}>{inner}</Link>;
  return <Link to="/books/$slug" params={{ slug: item.slug }} onClick={onPress} className={common}>{inner}</Link>;
}

function GateNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Unmapped</p>
      <h1 className="mt-4 font-serif text-4xl">That doorway is not among the forty-seven.</h1>
      <Link to="/gates" className="mt-8 inline-block rounded-full border border-gold px-6 py-3 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian">
        Return to the Gates
      </Link>
    </div>
  );
}
